import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import { features } from 'constants/featureFlags';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import useFileOperations from 'hooks/useFileOperations';
import { Control, FieldErrors, FieldValue, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ACCEPTED_ALL_FILE_TYPES, ACCEPTED_NON_VIDEO_FILE_TYPES } from 'utils/constants';
import { validateFiles } from 'utils/fileHelper';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ActionMenu } from '@thinkific/chalk/ActionMenu';
import { Button } from '@thinkific/chalk/Button';
import { InlineNotification } from '@thinkific/chalk/InlineNotification';
import { Code, PlusCircle, Smiley, TextAa, UploadSimple } from '@thinkific/chalk/react-icons';

import { BubbleMenu, EditorContent } from '@tiptap/react';

import LinkPreview from 'components/AttachmentLinkInput/LinkPreview';
import { AttachmentPreview } from 'components/AttachmentPreview/AttachmentPreview';
import { useCommunityContext } from 'components/CommunityContext';
import { FormValues as PostFormValues } from 'components/PostForm/PostForm';
import { FormValues as ReplyEditorFormValues } from 'components/ReplyEditorContext/ReplyEditorContext';
import { useGlobalState } from 'components/new/providers/GlobalStateProvider';

import { Attachment } from 'schema/Post/types';

import { useMentionSearch } from '../hooks/useEditor/Extensions/Mentions/useMentionSearch';
import { Content, useEditor } from '../hooks/useEditor/useEditor';
import ActionButton from './ActionButton';
import { button as attachmentButton } from './ActionButton.css';
import AttachmentInput from './Actions/AttachmentInput/AttachmentInput';
import EmojiPicker from './Actions/EmojiPicker/EmojiPicker';
import MenuBar from './Actions/MenuBar';
import * as css from './DocumentEditor.css';
import VideoEmbedModal from './VideoEmbedModal';

type AllFormValues = PostFormValues | ReplyEditorFormValues;

type DocumentEditorProps = {
  placeholder?: string;
  isEditing?: boolean;
  cancelEdit?: () => void;
  control: Control<AllFormValues>;
  errors: FieldErrors<AllFormValues>;
  setValue: (name: string, value: unknown) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  spaceId?: string;
  postType: 'post' | 'reply';
};

export default function DocumentEditor({
  placeholder,
  isEditing = false,
  cancelEdit,
  control,
  errors,
  setValue,
  onSubmit,
  isSubmitting,
  spaceId,
  postType,
}: DocumentEditorProps) {
  const { t } = useTranslation();
  const [videoEmbedModalOpen, setVideoEmbedModalOpen] = useState(false);
  const { handleSearch } = useMentionSearch();
  const { trackEvent, featureFlags } = useCommunityContext();
  const { isPostToolbarVisible, setIsPostToolbarVisible } = useGlobalState();
  const [error, setError] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const { field: bodyField } = useController({ control, name: 'body' });
  const { isUploading, uploadFiles, generatePreviews } = useFileOperations();
  const bodyFieldValue = useWatch({ control, name: 'body' });
  const attachments = useWatch({ control, name: 'attachments' });
  const { getDraftPost } = useGlobalState();

  const getAcceptedFileTypes = useMemo(() => {
    const isVideoUploadEnabled = featureFlags?.[features.POST_VIDEO_UPLOAD];
    return isVideoUploadEnabled ? ACCEPTED_ALL_FILE_TYPES : ACCEPTED_NON_VIDEO_FILE_TYPES;
  }, [featureFlags]);

  useEffect(() => {
    for (const key in errors) {
      if (key === 'body') {
        const message = errors[key]?.jsonContent?.message || errors[key]?.plainText?.message;
        setError(message as string);
      } else if (errors[key]?.message) {
        setError(errors[key].message as string);
      }
    }
  }, [errors]);

  const handleBodyChange = (value: Content, callback: (fieldValue: FieldValue<Body>) => void) => {
    callback({
      jsonContent: value.json,
      plainText: value.text,
    });
  };
  const { editor } = useEditor({
    content:
      (spaceId && postType === 'post' && !isEditing && getDraftPost(spaceId)?.body?.jsonContent) ||
      bodyFieldValue?.jsonContent ||
      undefined,
    onBlur: (value: Content) => handleBodyChange(value, bodyField.onChange),
    onChange: (value: Content) => {
      handleBodyChange(value, bodyField.onChange);
      setError(null);
    },
    onMentionsSearch: (search: string) => handleSearch(search, spaceId),
    version: 'editable',
    placeholder,
    className: css.editorContent,
    watchedAtttributes: [spaceId],
  });

  useEffect(() => {
    if (!isSubmitting && !bodyFieldValue?.jsonContent && editor) {
      editor.commands.clearContent();
    }
  }, [bodyFieldValue?.jsonContent, isSubmitting]);

  const trackFormatting = (type: string) => {
    trackEvent(TrackingEvent.FORMATTING_CLICKED, {
      [TrackingProperty.FORMATTING_TYPE]: type,
    });
  };

  const trackAttachment = (type: string) => {
    trackEvent(TrackingEvent.ATTACHMENT_CLICKED, {
      [TrackingProperty.ATTACHMENT]: type,
    });
  };

  const handleSubmit = () => {
    onSubmit?.();
  };

  const onFileSelected = async (files: File[]) => {
    const currentAttachments = control._formValues.attachments;
    setError(null);
    const validationError = validateFiles(files, currentAttachments || [], t);
    if (validationError) {
      setError(validationError);
      return;
    }

    const previews = generatePreviews(files);

    setValue('attachments', [...currentAttachments, ...previews]);

    const filesToUpload = files.map((file, index) => ({
      file,
      tempId: previews[index].tempId,
    }));

    const { attachments: uploadedAttachments, error: uploadError } = await uploadFiles(
      filesToUpload,
      {
        tags: {
          type: 'posts',
        },
      }
    );

    if (uploadError) {
      setError(uploadError);
    } else {
      // Replace the previews with the permanent attachments
      const existingAttachments = control._formValues.attachments;
      const updatedAttachments = existingAttachments.map((att) => {
        const found = uploadedAttachments.find((a) => a.tempId === att.tempId);
        if (found) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tempId, ...rest } = found;
          return rest;
        }
        return att;
      });

      setValue('attachments', updatedAttachments);
    }
  };

  const removeAttachment = (index: number) => {
    setValue(
      'attachments',
      attachments.filter((_, i) => i !== index)
    );
  };

  const handleFileUploadClick = () => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
    trackAttachment('file');
  };

  const handleVideoEmbedClick = () => {
    setVideoEmbedModalOpen(true);
    trackAttachment('link');
  };

  const handleVideoSelected = (attachment: Attachment) => {
    setValue('attachments', [...attachments, attachment]);
    setVideoEmbedModalOpen(false);
  };

  const hasLinkAttachment = attachments.some(
    (attachment) => attachment.attachmentType === PostAttachmentType.Video
  );

  const updateAltText = (altText: string, index: number) => {
    const currentAttachments = control._formValues.attachments;
    setValue(
      'attachments',
      currentAttachments.map((attachment, i) =>
        i === index ? { ...attachment, altText } : attachment
      )
    );
  };

  useEffect(() => {
    if (isEditing || postType === 'reply') return;
    const draftPost = spaceId && getDraftPost(spaceId);
    if (draftPost) {
      setValue('attachments', draftPost.attachments);
      if (draftPost.body) {
        setValue('body', draftPost.body);
      }
    }
  }, [spaceId]);

  return (
    <div className={css.document}>
      {editor && (
        <MenuBar
          className={!isPostToolbarVisible ? css.withoutToolbar : undefined}
          editor={editor}
          onActionClick={(action) => trackFormatting(action)}
        />
      )}
      {/* @note: BubbleMenu needs a parent div so it doesn't cause an error on unmount in TipTap v2. Fixed in v3 */}
      {editor && !isPostToolbarVisible && (
        <div>
          <BubbleMenu className={css.bubbleMenu} editor={editor}>
            <MenuBar editor={editor} onActionClick={(action) => trackFormatting(action)} />
          </BubbleMenu>
        </div>
      )}
      <div className={css.content}>
        <EditorContent className={css.editorContent} editor={editor} />
        {error && (
          <InlineNotification
            a11yImportance="assertive"
            actions={[]}
            description={error}
            title="Error"
            variant="danger"
            dismissible
            onDismiss={() => setError(null)}
          />
        )}
        {attachments.length > 0 && (
          <div className={css.attachmentContainer}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                attachment={attachment}
                attachmentType={attachment.attachmentType}
                key={attachment.id}
                t={t}
                onAltTextChange={(altText: string) => updateAltText(altText, index)}
                onRemove={() => removeAttachment(index)}
              />
            ))}
          </div>
        )}
        {/* Video Link preview for link attachments */}
        {attachments
          .filter((attachment) => attachment.attachmentType === PostAttachmentType.Video)
          .map((attachment) => {
            const attachmentIndex = attachments.indexOf(attachment);
            return (
              <LinkPreview
                key={`link-preview-${attachment.id || attachmentIndex}`}
                linkUrl={attachment.url || ''}
                onRemove={() => removeAttachment(attachmentIndex)}
              />
            );
          })}
        {/* Hidden file input for file upload */}
        <AttachmentInput
          accept={getAcceptedFileTypes}
          ariaControls="attachment-file-picker__input"
          dataQa="attachment-file-picker__input"
          ref={attachmentInputRef}
          multiple
          onFileSelected={onFileSelected}
        />
      </div>
      <div className={css.footerToolbar}>
        <div className={css.actions}>
          <ActionMenu
            dataQa="post-editor__add_attachment"
            direction="top"
            items={[
              {
                label: 'Upload a file',
                onClick: handleFileUploadClick,
                icon: UploadSimple,
              },
              {
                label: 'Embed video',
                onClick: handleVideoEmbedClick,
                disabled: hasLinkAttachment,
                icon: Code,
              },
            ]}
          >
            <Button
              className={attachmentButton}
              disabled={false}
              emphasis="low"
              icon={PlusCircle}
              size="sm"
              hiddenLabel
            >
              {t('components-documentEditor-menuBar_addAttachment', 'Add Attachment')}
            </Button>
          </ActionMenu>

          <ActionButton
            arialLabel={
              t('components-documentEditor-menuBar_showToolbar', 'Show Toolbar') as string
            }
            data-user-pilot-id="post-editor__show-toolbar"
            dataQa="post-editor__show-toolbar"
            disabled={false}
            icon={TextAa}
            isActive={isPostToolbarVisible}
            onClick={() => {
              trackFormatting('textFormatting');
              setIsPostToolbarVisible(!isPostToolbarVisible);
            }}
          />
          <EmojiPicker
            icon={Smiley}
            onClick={() => {
              trackFormatting('emoji');
            }}
            onSelect={(emoji) => {
              editor?.chain().focus().insertContent(emoji).run();
            }}
          />
        </div>
        <div className={css.submissions}>
          {isEditing && (
            <Button size="sm" type="button" variant="default" onClick={cancelEdit}>
              {t('Cancel', 'Cancel')}
            </Button>
          )}
          <Button
            dataQa="post-form__submit"
            disabled={isUploading}
            isLoading={isSubmitting}
            size="sm"
            type="submit"
            variant="primary"
            onClick={handleSubmit}
          >
            {isEditing
              ? t('components-richTextEditor-save', 'Save')
              : t('components-richTextEditor-post', 'Post')}
          </Button>
        </div>
      </div>

      <VideoEmbedModal
        attachmentUrl={null}
        open={videoEmbedModalOpen}
        onClose={() => setVideoEmbedModalOpen(false)}
        onSuccess={handleVideoSelected}
      />
    </div>
  );
}
